import { t } from '@glory/i18n';
import { Button, InfoHint, PageHeader, SettingsSection, SkeletonRows } from '@glory/ui';
import { formatPhoneInput } from '@glory/ui/fields/parsers';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Bell, FileSignature, Mail, Shield, UserRound } from 'lucide-react';

import { AvatarEditor } from '../../../features/team/avatar-editor.tsx';
import { RoleBadges } from '../../../features/team/employee-card.tsx';
import { useCrumbs } from '../../../lib/crumbs.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { usePermissions } from '../../../lib/permissions.ts';
import { meQuery } from '../../../lib/session.ts';

export const Route = createFileRoute('/_app/profile/')({
  component: ProfilePage,
});

/** Профиль: своё фото (меняет каждый сам), сведения о себе и вход в личные настройки. */
function ProfilePage() {
  const { data: me } = useSuspenseQuery(meQuery);
  const { can } = usePermissions();
  const employee = useQuery(orpc.org.team.get.queryOptions({ input: { id: me.id } }));
  useCrumbs([{ label: t('team.profileTitle') }]);

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader crumb={me.email} title={me.name} />
      <p className="-mt-2 text-muted">{t('team.profileLead')}</p>
      <SettingsSection
        title={
          <span className="inline-flex items-center gap-1.5">
            {t('team.photo')}
            <InfoHint label={t('team.photo')} hint={{ text: t('team.photoHint') }} />
          </span>
        }
        lead={t('team.photoLead')}
      >
        <AvatarEditor key={me.image ?? 'none'} person={me} />
      </SettingsSection>
      <SettingsSection
        title={t('team.aboutTitle')}
        lead={t('team.aboutLead')}
        action={
          <Button asChild size="sm" variant="ghost">
            <Link to="/team/$id" params={{ id: me.id }}>
              <UserRound /> {t('team.openCard')}
            </Link>
          </Button>
        }
      >
        {employee.isPending ? (
          <SkeletonRows rows={3} />
        ) : (
          <dl className="grid grid-cols-[minmax(7rem,auto)_minmax(0,1fr)] gap-x-4 gap-y-2 text-small">
            <dt className="text-muted">{t('audit.fields.position')}</dt>
            <dd className="font-semibold">{employee.data?.position ?? t('audit.emptyValue')}</dd>
            <dt className="text-muted">{t('team.phone')}</dt>
            <dd className="font-semibold tabular-nums">
              {employee.data?.phone ? formatPhoneInput(employee.data.phone) : t('audit.emptyValue')}
            </dd>
            <dt className="text-muted">{t('audit.fields.roles')}</dt>
            <dd className="flex flex-wrap gap-1">
              <RoleBadges roles={me.roles} />
            </dd>
          </dl>
        )}
      </SettingsSection>
      <SettingsSection title={t('team.personalTitle')}>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="secondary">
            <Link to="/profile/documents">
              <FileSignature /> {t('documents.employee.stepTitle')}
            </Link>
          </Button>
          <Button asChild size="sm" variant="secondary">
            <Link to="/settings/notifications">
              <Bell /> {t('shell.personalNotifications')}
            </Link>
          </Button>
          {can('mail.use') ? (
            <Button asChild size="sm" variant="secondary">
              <Link to="/profile/mail">
                <Mail /> {t('shell.personalMail')}
              </Link>
            </Button>
          ) : null}
          <Button asChild size="sm" variant="secondary">
            <Link to="/profile/security">
              <Shield /> {t('shell.security')}
            </Link>
          </Button>
        </div>
      </SettingsSection>
    </div>
  );
}
